import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { TextField, InputAdornment, FormHelperText } from '@mui/material';
import { Email, Send } from '@mui/icons-material';
import { Cookies } from 'react-cookie-consent';

import Button from '../components/formElements/Button';
import { useAuth } from '../context/AuthContext';

type ForgotPasswordFormProps = {
  setSubmitted: (submitted: boolean) => void;
};

type FormValues = {
  email: string;
};

function ForgotPasswordForm({ setSubmitted }: ForgotPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const { forgotPassword } = useAuth();
  const cookieConsent = Cookies.get('cookieconsent_essential');

  // Obserwowanie wartości pola email dla animacji
  const watchEmail = watch('email');

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    const toastId = toast.loading('Wysyłanie...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'colored',
    });

    try {
      await forgotPassword(data.email);
      toast.update(toastId, {
        render: 'Email z linkiem do resetowania hasła został wysłany!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      setSubmitted(true);
    } catch (error: Error | any) {
      console.error('Forgot password failed', error);
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas wysyłania emaila',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Animacje
  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col space-y-5 items-stretch w-full"
      variants={formVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <p className="text-gray-700 mb-4">
          Wprowadź adres email powiązany z Twoim kontem. Wyślemy Ci link, który
          umożliwi zresetowanie hasła.
        </p>

        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email jest wymagany',
            pattern: {
              value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
              message: 'Niepoprawny adres email',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email"
              variant="outlined"
              fullWidth
              error={!!errors.email}
              helperText={errors.email ? String(errors.email.message) : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email
                      color={
                        errors.email
                          ? 'error'
                          : watchEmail
                            ? 'primary'
                            : 'action'
                      }
                    />
                  </InputAdornment>
                ),
              }}
              className="bg-white rounded-lg"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '& fieldset': {
                    borderRadius: '0.5rem',
                  },
                },
              }}
            />
          )}
        />
      </motion.div>

      {cookieConsent !== 'true' && (
        <motion.div
          variants={itemVariants}
          className="bg-amber-50 border border-amber-200 rounded-lg p-3"
        >
          <FormHelperText className="!text-amber-700 !m-0">
            Aby zresetować hasło, musisz zaakceptować pliki cookie.
          </FormHelperText>
        </motion.div>
      )}

      <motion.div
        variants={itemVariants}
        whileHover={{ scale: cookieConsent === 'true' ? 1.02 : 1 }}
        whileTap={{ scale: cookieConsent === 'true' ? 0.98 : 1 }}
      >
        <Button
          type="submit"
          colorTheme="primary"
          disabled={cookieConsent !== 'true' || isLoading}
          className="w-full h-12 !rounded-lg transition-all duration-300 relative overflow-hidden"
        >
          <motion.div
            className="flex items-center justify-center gap-2 w-full"
            initial={false}
            animate={isLoading ? { x: -10 } : { x: 0 }}
          >
            <Send />
            <span>Wyślij link resetujący</span>
          </motion.div>

          {isLoading && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </motion.div>
          )}
        </Button>
      </motion.div>
    </motion.form>
  );
}

export default ForgotPasswordForm;
